import { useFormContext, useWatch } from "react-hook-form";

import { AutoFormFieldProps } from "@autoform/react";
import { XIcon } from "lucide-react";

import { RemoteSelect } from "@/components/fields/remote-select";
import { Badge } from "@/components/ui/badge";
import { ShowGenreNestedType } from "@/pages/movies/schemas/movie.nested.form";
import { useShowGenreRepository } from "@/pages/show-genres/hooks/use-show-genre-repository";

/**
 * Movie genre picker. Stores an array of `{ id, name }` ShowGenre refs (the
 * shape a movie carries); the repository flattens to `genreIds` on save.
 * Mirrors the music GenresField but searches the `show-genres` collection and
 * drops the music-specific popular-genre shortcuts.
 */
export function ShowGenresField({ id }: AutoFormFieldProps) {
  const { search } = useShowGenreRepository();
  const form = useFormContext();
  const value: ShowGenreNestedType[] = useWatch({ name: id }) ?? [];

  const add = (selected: ShowGenreNestedType | undefined) => {
    if (!selected?.id) return;
    if (value.some((g) => g.id === selected.id)) return;
    form.setValue(id, [...value, selected], { shouldDirty: true });
  };

  const remove = (genreId: string) => {
    form.setValue(
      id,
      value.filter((g) => g.id !== genreId),
      { shouldDirty: true },
    );
  };

  return (
    <div className="flex w-full flex-col space-y-2 text-foreground">
      <RemoteSelect<ShowGenreNestedType>
        className="w-[500px]"
        triggerType="change"
        onValueChanged={add}
        accessorFn={(option) => ({
          id: option?.id || "",
          name: option?.name ?? "",
        })}
        onSearch={async (query, onStatusChanged) => {
          if (!query) return [];
          onStatusChanged("loading");
          const genres = await search(query);
          onStatusChanged("success");
          return genres;
        }}
      />
      {value.length > 0 && (
        <div className="flex flex-row flex-wrap gap-2">
          {value.map((g) => (
            <Badge
              key={g.id}
              variant="secondary"
              className="gap-1 py-1 pl-2.5 pr-1.5"
            >
              {g.name}
              <button
                type="button"
                aria-label={`Remove ${g.name}`}
                className="rounded-full p-0.5 transition-colors hover:bg-foreground/15"
                onClick={() => remove(g.id)}
              >
                <XIcon className="size-3" />
              </button>
            </Badge>
          ))}
        </div>
      )}
    </div>
  );
}
